/**
 * Replaces the `postmonger` npm package (removed for W-23406380).
 *
 * Wire protocol is dictated by the Journey Builder host, which we don't
 * control: each message is `JSON.stringify`'d as `{ e: eventName, a1, a2, ... }`
 * and sent via `postMessage` between this iframe and `window.parent`. This
 * must match exactly what postmonger.js used to send/expect.
 *
 * Origin validation (SonarQube S2819 / CWE-346, "Verify the origin of the
 * received message" / "Specify a target origin for this message"):
 *
 * Journey Builder hosts this activity's iframe from a `*.marketingcloudapps.com`
 * subdomain on every MC stack (see README "QA and Prod Configuration" and the
 * `jbinteractions.qa1s1.marketingcloudapps.com` reference there, plus the
 * `*.marketingcloudapps.com` icon URL baked into `data/harness.html`'s mock
 * initEvent payload). `localhost` is additionally allowed so the local dev
 * harness (`npm run dev` + `/harness`, which loads this app in a same-origin
 * iframe) keeps working.
 *
 * We validate incoming messages against that allowlist in addition to the
 * existing `event.source === window.parent` check, and cache the first
 * validated origin to target outgoing `postMessage` calls precisely instead
 * of `'*'`. There's no single static host to hardcode ahead of time across
 * every stack, so outgoing messages sent before any trusted origin has been
 * observed (e.g. the very first message fired on mount) still use `'*'` --
 * a small, intentional residual gap called out in W-23406380.
 *
 * Hostname allowlist is checked via URL parsing (not a regex) so there's no
 * possible backtracking/ReDoS surface on attacker-controlled input.
 */
const TRUSTED_HOSTNAME_SUFFIX = '.marketingcloudapps.com';

function isTrustedOrigin(origin) {
	let url;
	try {
		url = new URL(origin);
	} catch (err) {
		return false;
	}

	if (url.protocol !== 'https:') {
		return false;
	}

	return url.hostname === 'localhost' ||
		url.hostname === 'marketingcloudapps.com' ||
		url.hostname.endsWith(TRUSTED_HOSTNAME_SUFFIX);
}

export function createSession() {
	const listeners = {};
	let trustedOrigin = null;

	function onMessage(event) {
		if (event.source !== window.parent) {
			return;
		}

		if (!isTrustedOrigin(event.origin)) {
			return;
		}

		trustedOrigin = event.origin;

		let data;
		try {
			data = JSON.parse(event.data);
		} catch (err) {
			return;
		}

		if (!data || !data.e) {
			return;
		}

		const { e: eventName, ...args } = data;
		const callback = listeners[eventName];
		if (callback) {
			callback(...Object.values(args));
		}
	}

	window.addEventListener('message', onMessage, false);

	return {
		on(eventName, callback) {
			listeners[eventName] = callback;
		},
		off(eventName) {
			delete listeners[eventName];
		},
		trigger(eventName, ...args) {
			const message = { e: eventName };
			args.forEach((arg, index) => {
				message[`a${index + 1}`] = arg;
			});
			// Falls back to '*' only until this session observes a trusted
			// origin (see the origin-validation note above; W-23406380).
			window.parent.postMessage(JSON.stringify(message), trustedOrigin || '*');
		},
		destroy() {
			window.removeEventListener('message', onMessage, false);
		}
	};
}
